import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import axios from "axios";
import { Field, FormConfig } from "../types";
import { createEmptyItem, getNestedValue } from "../utils/helpers";
import getFormConfig from "../formConfig";
import useDialog from "../../../../hooks/useDialog";

interface UseFormValidationProps {
  editing: boolean;
  universalID?: string;
}

const useFormValidation = ({ editing, universalID }: UseFormValidationProps) => {
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  const [generalInfo, setGeneralInfo] = useState<any>(null);
  const [isLeasing, setIsLeasing] = useState<boolean>(false);
  const [loading, setLoading] = useState(editing);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const isUpdatingRef = useRef(false);
  const fieldDepsRef = useRef<Record<string, string>>({});
  const prevFormValuesRef = useRef<any>({});

  const formConfig = useMemo<FormConfig>(() => getFormConfig(generalInfo, isLeasing), [generalInfo, isLeasing]);
  const { showSnackbar } = useDialog() as { showSnackbar: (message: string, severity: string) => void };

  /* eslint-disable react-hooks/rules-of-hooks */
  const multiGroupFieldArrays = formConfig.groups
    .filter((g) => g.isMultiGroup)
    .reduce((acc, group) => {
      acc[group.name] = useFieldArray({ control, name: group.name });
      return acc;
    }, {} as Record<string, ReturnType<typeof useFieldArray>>);

  const multiFieldArrays = useMemo(() => multiGroupFieldArrays, [multiGroupFieldArrays]);
  const formValues = watch();

  useEffect(() => {
    const handleGeneralInfo = (data: any) => {
      setGeneralInfo(data);
      try {
        const bank = JSON.parse(localStorage.getItem("bank") || "{}");
        setIsLeasing(!!bank.leasing);
      } catch {
        setIsLeasing(false);
      }
    };

    const requests = [axios.get("/general_info")];
    if (editing && universalID) {
      requests.push(axios.get(`/application/new_universal/${universalID}`));
    }

    Promise.all(requests)
      .then((responses) => {
        handleGeneralInfo(responses[0].data);
        if (responses.length > 1) {
          applicationDataRef.current = responses[1].data;
        }
      })
      .catch(console.error);
  }, [editing, universalID]);

  const initializeParentObjectsRef = useRef(false);
  const dataLoadedRef = useRef(false);
  const applicationDataRef = useRef<any>(null);
  
  useEffect(() => {
    if (!initializeParentObjectsRef.current && formConfig && formConfig.fields.length > 0) {
      const checkboxFields = formConfig.fields.filter((field) => field.type === "checkbox" && field.name.includes(".") && typeof field.value === "function");

      if (checkboxFields.length > 0) {
        const parentPaths = checkboxFields.map((field) => {
          const parts = field.name.split(".");
          return parts.slice(0, -1).join(".");
        });

        parentPaths.forEach((path) => {
          if (!getNestedValue(formValues, path)) {
            setValue(path, {}, { shouldDirty: false });
          }
        });

      }

      initializeParentObjectsRef.current = true;
    }
  }, [formConfig, formValues, setValue]);

  useEffect(() => {
    if (!editing || !universalID || !generalInfo) return;
    if (dataLoadedRef.current) return;
    if (!applicationDataRef.current) return; // Wait for cached data
    
    dataLoadedRef.current = true;
    
    const data = applicationDataRef.current;
    
    const processValue = (key: string, value: any) => {
      if (typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/)) {
        // Convert date to 'YYYY-MM-DD'
        const formattedDate = new Date(value).toISOString().split("T")[0];
        setValue(key, formattedDate);
      } else if (Array.isArray(value)) {
        // Skip arrays here - they'll be handled separately for multi-group fields
        return;
      } else if (value && typeof value === "object") {
        if (value.hasOwnProperty("_id")) {
          // Handle nested objects with '_id' (like selectors)
          setValue(key, value._id);
        } else {
          // Iterate through nested objects
          Object.keys(value).forEach((subKey) => {
            processValue(`${key}.${subKey}`, value[subKey]);
          });
        }
      } else {
        setValue(key, value);
      }
    };

    // Process all non-array fields
    Object.keys(data).forEach((key) => {
      if (!Array.isArray(data[key])) {
        processValue(key, data[key]);
      }
    });

    // Handle attachments - extract file paths from objects
    if (data.attachments && typeof data.attachments === 'object') {
      Object.entries(data.attachments).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          const paths = value.map((item: any) => 
            typeof item === 'string' ? item : item.original_path || item.signed_url || item
          );
          setValue(`attachments.${key}`, paths);
        }
      });
    }

    // Handle multi-group arrays
    const extractId = (item: any) => (item && typeof item === "object" && "_id" in item ? item._id : item);
    
    formConfig.groups
      .filter((g) => g.isMultiGroup)
      .forEach((g) => {
        // Get array data (handle nested paths)
        let arrayData = data[g.name];
        if (!arrayData && g.name.includes('.')) {
          arrayData = g.name.split('.').reduce((obj, key) => obj?.[key], data);
        }
        
        if (!Array.isArray(arrayData) || !arrayData.length) return;
        
        // Check if this is a primitive array (isMultiField)
        const isPrimitive = formConfig.fields.some((f) => f.group === g.name && f.name === g.name && f.isMultiField);
        
        const processedArray = arrayData.map((item: any) => {
          if (isPrimitive) return extractId(item);
          
          // Process object items - extract _id from nested objects
          return Object.fromEntries(
            Object.entries(item).map(([key, val]) => [key, extractId(val)])
          );
        });
        
        setValue(g.name, processedArray);
      });
    
    // All data processed, turn off loading
    setLoading(false);
  }, [editing, universalID, generalInfo, setValue, formConfig.groups, formConfig.fields, multiFieldArrays]);

  const handleBlur = useCallback(
    (field: Field, path?: string) => {
      const key = path || field.name;
      const value = watch(key);
      const all = watch();
      Promise.resolve(field.validation?.(value, all)).then((err) => {
        setValidationErrors((prev) => ({ ...prev, [key]: err || "" }));
      });
    },
    [watch]
  );

  const processFieldUpdates = useCallback(async () => {
    if (isUpdatingRef.current) return;
    if (!Object.keys(prevFormValuesRef.current).length) {
      prevFormValuesRef.current = { ...formValues };
      return;
    }
    isUpdatingRef.current = true;
    try {
      // Handle clearing invisible field values for both regular and multi-group fields
      formConfig.fields.forEach((field) => {
        const group = field.group ? formConfig.groups.find((g) => g.name === field.group) : null;
        
        let isFieldVisible = true;
        
        if (group?.visibility && typeof group.visibility === "function") {
          isFieldVisible = group.visibility(formValues);
        }
        
        if (isFieldVisible && field.visibility && typeof field.visibility === "function") {
          if (group?.isMultiGroup && formValues[group.name]) {
            formValues[group.name].forEach((item: any, index: number) => {
              const isVisible = (field.visibility as (data: any, index?: number) => boolean)(formValues, index);
              if (!isVisible) {
                const fieldNamePart = field.name.includes(group.name) ? field.name.split(".").slice(1).join(".") : field.name;
                const fieldPath = `${group.name}.${index}.${fieldNamePart}`;
                const currentValue = getNestedValue(formValues, fieldPath);
                if (currentValue !== undefined && currentValue !== null && currentValue !== "") {
                  setValue(fieldPath, "", { shouldDirty: false });
                }
              }
            });
            return; 
          } else {
            isFieldVisible = field.visibility(formValues);
          }
        }
        
        // Clear the field if it's not visible (for regular fields and groups)
        if (!isFieldVisible && !group?.isMultiGroup) {
          const currentValue = getNestedValue(formValues, field.name);
          if (currentValue !== undefined && currentValue !== null && currentValue !== "") {
            setValue(field.name, undefined, { shouldDirty: false });
          }
        }
      });

      for (const field of formConfig.fields) {
        if (typeof field.value !== "function") continue;

        const group = field.group ? formConfig.groups.find((g) => g.name === field.group) : null;

        if (group?.isMultiGroup) continue;

        let isVisible = true;
        if (group?.visibility && typeof group.visibility === "function") {
          isVisible = group.visibility(formValues);
        }
        if (isVisible && field.visibility && typeof field.visibility === "function") {
          isVisible = field.visibility(formValues);
        }

        if (!isVisible) continue;

        const deps = field.dependencies || [];
        const depsObj = deps.reduce((o: any, d) => {
          o[d] = getNestedValue(formValues, d);
          return o;
        }, {});
        const depsKey = JSON.stringify(depsObj);
        if (fieldDepsRef.current[field.name] === depsKey) continue;
        fieldDepsRef.current[field.name] = depsKey;

        if (field.type === "checkbox" && field.name.includes(".")) {
          try {
            const res = field.value(formValues);
            const newVal = res instanceof Promise ? await res : res;

            const curr = getNestedValue(formValues, field.name);

            if (JSON.stringify(newVal) !== JSON.stringify(curr)) {
              setValue(field.name, newVal, { shouldDirty: true });
            }

          } catch (error) {
            console.error(`Error processing checkbox value for ${field.name}:`, error);
          }
        } else {
          const res = field.value(formValues);
          const newVal = res instanceof Promise ? await res : res;
          const curr = getNestedValue(formValues, field.name);

          if (JSON.stringify(newVal) !== JSON.stringify(curr)) {
            setValue(field.name, newVal, { shouldDirty: false });
            if (field.validation) handleBlur(field, field.name);
          }
        }
      }
    } finally {
      prevFormValuesRef.current = { ...formValues };
      isUpdatingRef.current = false;
    }
  }, [formValues, formConfig.fields, setValue, handleBlur, formConfig.groups, watch]);

  useEffect(() => {
    if (!Object.keys(formValues).length) return;
    const id = setTimeout(processFieldUpdates, 50);
    return () => clearTimeout(id);
  }, [formValues, processFieldUpdates]);

  useEffect(() => {
    if (!generalInfo) return;
    Object.entries(multiFieldArrays).forEach(([name, arr]) => {
      const group = formConfig.groups.find((g) => g.name === name);
      const isGroupVisible = !group?.visibility || (typeof group.visibility === "function" && group.visibility(formValues));

      if (isGroupVisible) {
        if (!arr.fields.length) {
          const isPrimitive = formConfig.fields.find((f) => f.group === name)?.isMultiField;
          arr.append(isPrimitive ? "" : createEmptyItem(formConfig.fields, name));
        }
      } else {
        if (arr.fields.length > 0) {
          for (let i = arr.fields.length - 1; i >= 0; i--) {
            arr.remove(i);
          }
        }
      }
    });
  }, [generalInfo, formConfig.fields, formConfig.groups, multiFieldArrays, formValues]);

  const onSubmit = useCallback(
    (data: any) => {
      setLoading(true);
      if (Object.values(validationErrors).some(Boolean)) {
        setLoading(false);
        showSnackbar("გთხოვთ გაასწოროთ ვალიდაციის შეცდომები", "error");
        return;
      }

      const cleanedData = { ...data };

      // Remove fields from invisible groups
      formConfig.groups.forEach((group) => {
        const isGroupVisible = !group.visibility || (typeof group.visibility === "function" && group.visibility(formValues));

        if (!isGroupVisible && !group.isMultiGroup) {
          const groupFields = formConfig.fields.filter((field) => field.group === group.name);
          groupFields.forEach((field) => {
            if (field.name.includes(".")) {
              const parts = field.name.split(".");
              let current = cleanedData;
              for (let i = 0; i < parts.length - 1; i++) {
                if (!current[parts[i]]) return; // Path doesn't exist, skip
                current = current[parts[i]];
              }
              if (current && current.hasOwnProperty(parts[parts.length - 1])) {
                delete current[parts[parts.length - 1]];
              }
            } else {
              delete cleanedData[field.name];
            }
          });
        }
      });

      // Remove individual invisible fields
      formConfig.fields.forEach((field) => {
        const group = field.group ? formConfig.groups.find((g) => g.name === field.group) : null;
        
        let isFieldVisible = true;
        
        // Check group visibility first
        if (group?.visibility && typeof group.visibility === "function") {
          isFieldVisible = group.visibility(formValues);
        }
        
        // Then check field visibility
        if (isFieldVisible && field.visibility && typeof field.visibility === "function") {
          isFieldVisible = field.visibility(formValues);
        }
        
        // Remove the field if it's not visible
        if (!isFieldVisible) {
          if (field.name.includes(".")) {
            const parts = field.name.split(".");
            let current = cleanedData;
            for (let i = 0; i < parts.length - 1; i++) {
              if (!current[parts[i]]) return; // Path doesn't exist, skip
              current = current[parts[i]];
            }
            if (current && current.hasOwnProperty(parts[parts.length - 1])) {
              delete current[parts[parts.length - 1]];
            }
          } else {
            delete cleanedData[field.name];
          }
        }
      });

      const json = JSON.stringify(cleanedData).replace(/"undefined"/g, "null");
      axios
        .post(editing ? `/bank/application/edit_new_universal/${universalID}` : "/bank/application/submit_new_universal", JSON.parse(json))
        .then((res) => {
          showSnackbar(res.data.message, "success");
          if (editing) {
            // Redirect to the detailed view after successful edit
            setTimeout(() => {
              window.location.href = `/new-universal-application/${universalID}`;
            }, 1500);
          }
        })
        .catch((err) => {
          if (err.response?.data?.message) {
            showSnackbar(err.response.data.message, "error");
          } else {
            showSnackbar("დაფიქსირდა შეცდომა", "error");
          }
        })
        .finally(() => setLoading(false));
    },
    [editing, universalID, validationErrors, showSnackbar, formConfig, formValues]
  );

  return {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    errors,
    generalInfo,
    loading,
    validationErrors,
    multiFieldArrays,
    onSubmit,
    formValues,
    handleBlur,
    formConfig,
    isLeasing,
  };
};

export default useFormValidation;
